"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import { StatsMetaline, type StatsMetalineItem } from "@/components/ui/stats-metaline"
import { Skeleton } from "@/components/ui/skeleton"
import { MyTasksWidget } from "@/components/dashboard/my-tasks-widget"
import { RevenueTrendChart } from "@/components/dashboard/revenue-trend-chart"
import { InvoiceStatusChart } from "@/components/dashboard/invoice-status-chart"
import { QuotesFunnelChart } from "@/components/dashboard/quotes-funnel-chart"
import { useDashboardWidgetFlags } from "@/components/dashboard/use-dashboard-widget-flags"
import { usePermissions } from "@/hooks/use-permissions"
import { useFormatCurrency } from "@/hooks/use-currency"
import { cn } from "@/lib/utils"

import { getInvoiceStats, getRecentInvoices, type InvoiceStats } from "@/lib/invoices-api"
import { getQuoteStats } from "@/lib/quotes-api"
import type { Invoice, PaginatedResponse, QuoteStats } from "@/types"

import { getOverdueInvoices } from "./accountant/finance-api"
import { OverdueInvoicesList } from "./accountant/overdue-invoices-list"
import { RecentInvoicesList } from "./accountant/recent-invoices-list"

/** Rows pulled into each invoice rail. */
const LIST_LIMIT = 5

interface FinanceData {
  invoiceStats: InvoiceStats | null
  overdue: PaginatedResponse<Invoice> | null
  recent: PaginatedResponse<Invoice> | null
  quoteStats: QuoteStats | null
}

const EMPTY_DATA: FinanceData = {
  invoiceStats: null,
  overdue: null,
  recent: null,
  quoteStats: null,
}

/**
 * Accountant ("Finance") dashboard — a money-first variant that replaces the
 * shared leads/appointments headline band with an invoice KPI strip and stacks
 * the finance surfaces in cash-flow order:
 *
 *   band  revenue this month · outstanding · overdue · drafts
 *   row 1 revenue trend (hero, 2 cols) + invoice status mix (1 col)
 *   row 2 overdue chase list
 *   row 3 latest invoices (2 cols) + quote pipeline as future revenue (1 col)
 *
 * DATA CONTRACT: the KPI band and BOTH row-1 charts are fed by ONE
 * `getInvoiceStats()` response — the tiles never re-add bucket totals by hand
 * (`outstanding_total` is already the unpaid remainder of the sent + partial
 * buckets, and `overdue.total` the remaining balance past due date).
 *
 * PERMISSIONS: nothing is requested until `usePermissions().isLoaded`; every
 * invoice surface is gated on `invoices.view` and the quote funnel on
 * `quotes.view`. An ungranted widget is OMITTED, not rendered empty.
 */
export function AccountantDashboard() {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { isLoaded, can } = usePermissions()
  const { formatCurrencyNoDecimals } = useFormatCurrency()

  const [data, setData] = useState<FinanceData>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(true)

  const canViewInvoices = can("invoices.view")
  const canViewQuotes = can("quotes.view")
  const { showQuoteFunnel } = useDashboardWidgetFlags()
  // The grant still drives the FETCH (the payload is cheap and shared); the
  // flag only decides whether the funnel is laid out — so a trimmed clinic
  // gives the full row to the invoice list instead of leaving a third empty.
  const showQuotes = canViewQuotes && showQuoteFunnel

  useEffect(() => {
    // Hold every request until the permission set has resolved — firing first
    // and discarding would 403 for users who legitimately lack the grant.
    if (!isLoaded) return

    let cancelled = false
    const loadFinance = async () => {
      setIsLoading(true)
      try {
        const [invoiceStats, overdue, recent, quoteStats] = await Promise.all([
          canViewInvoices ? getInvoiceStats().catch(() => null) : Promise.resolve(null),
          canViewInvoices
            ? getOverdueInvoices(LIST_LIMIT).catch(() => null)
            : Promise.resolve(null),
          canViewInvoices
            ? getRecentInvoices(LIST_LIMIT).catch(() => null)
            : Promise.resolve(null),
          canViewQuotes ? getQuoteStats().catch(() => null) : Promise.resolve(null),
        ])
        if (!cancelled) setData({ invoiceStats, overdue, recent, quoteStats })
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    loadFinance()

    return () => {
      cancelled = true
    }
  }, [isLoaded, canViewInvoices, canViewQuotes])

  const stats = data.invoiceStats

  // Bucket reads stay defensive (`?.`) so a clinic whose /invoices/stats still
  // ships an older vocabulary renders zeros instead of throwing.
  const paidThisMonth = stats?.paid_this_month ?? 0
  const outstandingTotal = stats?.outstanding_total ?? 0
  const awaitingCount =
    (stats?.by_status?.sent?.count ?? 0) + (stats?.by_status?.partial?.count ?? 0)
  const overdueCount = stats?.overdue?.count ?? 0
  const overdueTotal = stats?.overdue?.total ?? 0
  const draftCount = stats?.by_status?.draft?.count ?? 0
  const draftTotal = stats?.by_status?.draft?.total ?? 0

  // Localized current-month name for the revenue tile's sub line.
  const monthName = formatter.dateTime(new Date(), { month: "long" })

  const kpiItems: StatsMetalineItem[] = useMemo(() => {
    const skeletonValue = <Skeleton className="h-[13px] w-12" />
    return [
      {
        key: "revenue",
        label: t("financeKpi.revenue.label"),
        value: isLoading ? skeletonValue : formatCurrencyNoDecimals(paidThisMonth),
        sub: isLoading ? undefined : t("financeKpi.revenue.hint", { month: monthName }),
        accent: !isLoading && paidThisMonth > 0 ? "success" : "default",
      },
      {
        key: "outstanding",
        label: t("financeKpi.outstanding.label"),
        value: isLoading ? skeletonValue : formatCurrencyNoDecimals(outstandingTotal),
        sub: isLoading
          ? undefined
          : t("financeKpi.outstanding.hint", { count: awaitingCount }),
      },
      {
        key: "overdue",
        label: t("financeKpi.overdue.label"),
        value: isLoading ? skeletonValue : overdueCount.toLocaleString(),
        sub: isLoading
          ? undefined
          : t("financeKpi.overdue.hint", {
              amount: formatCurrencyNoDecimals(overdueTotal),
            }),
        accent: !isLoading && overdueCount > 0 ? "danger" : "default",
      },
      {
        key: "drafts",
        label: t("financeKpi.drafts.label"),
        value: isLoading ? skeletonValue : draftCount.toLocaleString(),
        sub: isLoading
          ? undefined
          : t("financeKpi.drafts.hint", {
              amount: formatCurrencyNoDecimals(draftTotal),
            }),
      },
    ]
    // `formatCurrencyNoDecimals` closes over the cached settings query and `t`
    // over the locale — the band is keyed off the raw figures instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    isLoading,
    paidThisMonth,
    outstandingTotal,
    awaitingCount,
    overdueCount,
    overdueTotal,
    draftCount,
    draftTotal,
    monthName,
  ])

  return (
    <ApolloPageShell>
      <ApolloPageHeader
        eyebrow={t("page.eyebrow")}
        title={t("variants.accountant.title")}
        stats={canViewInvoices ? <StatsMetaline items={kpiItems} /> : undefined}
      />

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
          {/* My Tasks — self-gating (tasks_enabled + tasks.view.assigned). */}
          <MyTasksWidget />

          {/* Row 1 — revenue hero + status mix, one shared InvoiceStats fetch */}
          {canViewInvoices && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
              <RevenueTrendChart
                data={data.invoiceStats}
                isLoading={isLoading}
                className="lg:col-span-2"
              />
              <InvoiceStatusChart data={data.invoiceStats} isLoading={isLoading} />
            </div>
          )}

          {/* Row 2 — what needs chasing today */}
          {canViewInvoices && (
            <OverdueInvoicesList
              data={data.overdue?.data ?? null}
              total={data.overdue?.meta?.total}
              isLoading={isLoading}
            />
          )}

          {/* Row 3 — latest invoicing activity + the future-revenue pipeline */}
          {(canViewInvoices || showQuotes) && (
            <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
              {canViewInvoices && (
                <RecentInvoicesList
                  data={data.recent?.data ?? null}
                  isLoading={isLoading}
                  className={showQuotes ? "lg:col-span-2" : "lg:col-span-3"}
                />
              )}
              {showQuotes && (
                <div className={cn(canViewInvoices ? undefined : "lg:col-span-3")}>
                  <QuotesFunnelChart data={data.quoteStats} isLoading={isLoading} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ApolloPageShell>
  )
}
